import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useStaticTranslation } from "@/lib/staticTranslations";

// Import font preview images
import fontA from "@/assets/fonts/font-a.webp";
import fontB from "@/assets/fonts/font-b.webp";
import fontC from "@/assets/fonts/font-c.webp";
import fontD from "@/assets/fonts/font-d.webp";
import fontE from "@/assets/fonts/font-e.webp";
import fontF from "@/assets/fonts/font-f.webp";

export interface EngravingData {
  font: string;
  texts: string[]; // Array of texts for multiple engraving locations
}

interface EngravingOptionsProps {
  value: EngravingData | null;
  onChange: (data: EngravingData | null) => void;
  price: number;
  currencyCode: string;
  engravingCount: number; // Number of engraving fields to show
  errors?: {
    font?: string;
    texts?: string[];
  };
}

const ENGRAVING_FONTS = [
  { id: "font-a", name: "פונט A", image: fontA },
  { id: "font-b", name: "פונט B", image: fontB },
  { id: "font-c", name: "פונט C", image: fontC },
  { id: "font-d", name: "פונט D", image: fontD },
  { id: "font-e", name: "פונט E", image: fontE },
  { id: "font-f", name: "פונט F", image: fontF },
];

export const ENGRAVING_PRICE = 0; // Engraving is free

export const EngravingOptions = ({
  value,
  onChange,
  price,
  currencyCode,
  engravingCount,
  errors,
}: EngravingOptionsProps) => {
  const { t, isHebrew } = useStaticTranslation("engraving");
  const [selectedFont, setSelectedFont] = useState<string>(value?.font || "");
  const [engravingTexts, setEngravingTexts] = useState<string[]>(
    value?.texts || Array(engravingCount).fill("")
  );

  useEffect(() => {
    setEngravingTexts(prev => {
      const newTexts = [...prev];
      while (newTexts.length < engravingCount) {
        newTexts.push("");
      }
      return newTexts.slice(0, engravingCount);
    });
  }, [engravingCount]);

  const handleFontChange = (fontId: string) => {
    setSelectedFont(fontId);
    onChange({
      font: fontId,
      texts: engravingTexts,
    });
  };

  const handleTextChange = (index: number, text: string) => {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length > 3 && text.length > (engravingTexts[index]?.length || 0)) {
      return;
    }
    
    const newTexts = [...engravingTexts];
    newTexts[index] = text;
    setEngravingTexts(newTexts);
    onChange({
      font: selectedFont,
      texts: newTexts,
    });
  };

  const getWordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

  const getRecommendationText = () => {
    if (engravingCount === 2) return t("rec2", "ניתן לחרוט עד 3 מילים בכל שדה. לדוגמה: שדה 1 - \"אני אוהב אותך\", שדה 2 - \"לנצח שלך\"");
    if (engravingCount === 3) return t("rec3", "ניתן לחרוט עד 3 מילים בכל שדה. לדוגמה: מילה אחת בכל שדה, או משפט קצר בשדה אחד");
    if (engravingCount === 4) return t("rec4", "ניתן לחרוט עד 3 מילים בכל שדה. לדוגמה: שם בשדה 1, תאריך בשדה 2, מסר קצר בשדות נוספים");
    if (engravingCount >= 5) return t("rec5", `ניתן לחרוט עד 3 מילים בכל אחד מ-${engravingCount} השדות. מומלץ לחלק את המסר בין השדות`);
    return "";
  };

  const getPlaceholderExample = (index: number) => {
    if (engravingCount === 1) return t("example1", "לדוגמה: אני אוהב אותך");
    if (engravingCount === 2) {
      return index === 0 ? t("example2a", "לדוגמה: אני אוהב אותך") : t("example2b", "לדוגמה: לנצח שלך");
    }
    if (engravingCount === 3) {
      const examples = [t("example3a", "לדוגמה: אהבה"), t("example3b", "לדוגמה: לנצח"), t("example3c", "לדוגמה: שלנו")];
      return examples[index] || t("exampleDefault", "לדוגמה: טקסט");
    }
    if (engravingCount >= 4) {
      const examples = [t("example4a", "לדוגמה: שם"), t("example4b", "לדוגמה: תאריך"), t("example4c", "לדוגמה: אהבה"), t("example4d", "לדוגמה: לנצח")];
      return examples[index] || t("exampleDefault", "לדוגמה: טקסט");
    }
    return "";
  };

  const dir = isHebrew ? "rtl" : "ltr";

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-5 p-6 bg-gradient-to-br from-secondary/30 via-secondary/20 to-transparent border border-border/50 shadow-sm"
      dir={dir}
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
          <svg 
            className="w-5 h-5 text-primary" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" 
            />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-medium text-foreground">{t("title", "חריטה אישית")}</h3>
          <p className="text-xs text-muted-foreground">
            {engravingCount > 1 
              ? `${engravingCount} ${t("multipleLocations", "מיקומי חריטה זמינים")}` 
              : t("subtitle", "הוסף מגע אישי לתכשיט שלך")}
          </p>
        </div>
      </div>

      {/* Font Selection */}
      <div className="space-y-3">
        <Label className="text-sm text-muted-foreground">
          {t("selectFont", "בחירת סגנון כתב")} <span className="text-primary">{t("required", "*")}</span>
        </Label>
        <div className="grid grid-cols-6 gap-1.5">
          {ENGRAVING_FONTS.map((font, index) => (
            <motion.button
              key={font.id}
              type="button"
              onClick={() => handleFontChange(font.id)}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2, delay: index * 0.03 }}
              className={cn(
                "relative aspect-square transition-all overflow-hidden bg-background p-1 shadow-sm",
                selectedFont === font.id
                  ? "ring-2 ring-primary ring-offset-1 ring-offset-background border-transparent"
                  : "border border-border/60 hover:border-primary/40 hover:shadow-md",
                errors?.font && !selectedFont && "ring-2 ring-destructive ring-offset-1"
              )}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.95 }}
            >
              <img
                src={font.image}
                alt={font.name}
                className="w-[85%] h-[85%] m-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              />
              {selectedFont === font.id && (
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-0.5 right-0.5 w-3 h-3 bg-primary flex items-center justify-center"
                >
                  <svg className="w-2 h-2 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          ))}
        </div>
        {errors?.font && !selectedFont && (
          <p className="text-sm text-destructive">{errors.font}</p>
        )}
      </div>

      {/* Engraving Recommendation */}
      {engravingCount > 1 && (
        <div className="p-3 bg-primary/5 border border-primary/20 text-sm">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <div>
              <span className="font-medium text-foreground">{t("recommendation", "המלצה:")}</span>{" "}
              <span className="text-muted-foreground">{getRecommendationText()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Engraving Text Inputs */}
      {Array.from({ length: engravingCount }).map((_, index) => {
        const text = engravingTexts[index] || "";
        const wordCount = getWordCount(text);
        const hasError = errors?.texts?.[index];
        const isRequired = engravingCount === 1 || index === 0;
        
        return (
          <div key={index} className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">
                {engravingCount > 1 ? `${t("engravingTextNum", "טקסט לחריטה")} ${index + 1}` : t("engravingText", "טקסט לחריטה")} {isRequired && <span className="text-primary">{t("required", "*")}</span>}
              </Label>
              <span className={cn(
                "text-xs px-2 py-0.5",
                wordCount === 3 
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" 
                  : "bg-secondary text-muted-foreground"
              )}>
                {wordCount}/3 {t("words", "מילים")}
              </span>
            </div>
            <Input
              type="text"
              placeholder={`${getPlaceholderExample(index)} ${t("upTo3Words", "(עד 3 מילים)")}${!isRequired ? ` ${t("optional", "- אופציונלי")}` : ''}`}
              value={text}
              onChange={(e) => handleTextChange(index, e.target.value)}
              className={cn(
                "h-12 border-border/60 bg-background/80 focus:bg-background transition-colors",
                isHebrew ? "text-right" : "text-left",
                hasError && !text.trim() && isRequired && "ring-2 ring-destructive ring-offset-2"
              )}
              dir={dir}
            />
            {hasError && !text.trim() && isRequired && (
              <p className="text-sm text-destructive">{hasError}</p>
            )}
          </div>
        );
      })}
      
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {engravingCount > 1 
          ? t("infoMultiple", "הטקסטים יחרטו על התכשיט במיקומים השונים בסגנון שבחרת")
          : t("infoSingle", "הטקסט יחרט על התכשיט בסגנון שבחרת")}
      </p>
    </motion.div>
  );
};

// Helper function to get engraving count from tags
export const getEngravingCount = (tags: string[] | string | undefined): number => {
  if (!tags) return 0;
  
  // Handle both array and comma-separated string formats
  const tagList = Array.isArray(tags) 
    ? tags 
    : typeof tags === 'string' 
      ? tags.split(',').map(t => t.trim())
      : [];
  
  // Check for numbered engraving tags (חריטה 2, חריטה 3, etc.)
  for (const tag of tagList) {
    const lowerTag = tag.toLowerCase().trim();
    
    // Check Hebrew format: חריטה 2, חריטה 3, etc.
    const hebrewMatch = lowerTag.match(/^חריטה\s*(\d+)$/);
    if (hebrewMatch) {
      return parseInt(hebrewMatch[1], 10);
    }
    
    // Check English format: engraving 2, engraving 3, etc.
    const englishMatch = lowerTag.match(/^engraving\s*(\d+)$/);
    if (englishMatch) {
      return parseInt(englishMatch[1], 10);
    }
  }
  
  // Check for simple engraving tag (no number = 1 field)
  const hasSimpleTag = tagList.some(tag => {
    const lowerTag = tag.toLowerCase().trim();
    return lowerTag === 'חריטה' || lowerTag === 'engraving';
  });
  
  return hasSimpleTag ? 1 : 0;
};

// Legacy function for backwards compatibility
export const hasEngravingTag = (tags: string[] | string | undefined): boolean => {
  return getEngravingCount(tags) > 0;
};

// Validate engraving data
export const validateEngraving = (
  data: EngravingData | null, 
  engravingCount: number
): { font?: string; texts?: string[] } => {
  const errors: { font?: string; texts?: string[] } = {};
  
  if (!data?.font) {
    errors.font = "יש לבחור פונט";
  }
  
  const textErrors: string[] = [];
  // Only first field is required when there are multiple fields
  const requiredCount = engravingCount === 1 ? 1 : 1; // Only first field required
  
  for (let i = 0; i < requiredCount; i++) {
    if (!data?.texts?.[i]?.trim()) {
      textErrors[i] = `יש להזין טקסט לחריטה ${engravingCount > 1 ? i + 1 : ""}`.trim();
    }
  }
  
  if (textErrors.some(e => e)) {
    errors.texts = textErrors;
  }
  
  return errors;
};
